import { ColumnDef } from "@tanstack/react-table";
import {
	GetCommunityAnnouncementsForProQuery,
	GetCommunityJobOpportunitiesForProQuery,
} from "../../__generated__/graphql";
import { formatLocation, formatEmploymentType, formatAsCurrency } from "../../utlis/helpers";
import { Button } from "../../components/ui/button";
import { Link } from "react-router-dom";
import { formatAnnouncementType } from "src/utlis/helpers";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";

export const communityJobOpportunitiesColumns: ColumnDef<
	GetCommunityJobOpportunitiesForProQuery["getCommunityJobOpportunitiesForPro"][0]
>[] = [
	{
		accessorKey: "title",
		header: "Title",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[14px] lg:text-[16px] text-gray-500">
				<Link
					to={`/opportunities/communities/${row.original.id}`}
					className="font-medium hover:underline"
				>
					{row.original.title}
				</Link>
			</div>
		),
	},
	{
		accessorKey: "organization.name",
		header: "Community",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				{row.original.organization.name}
			</div>
		),
	},
	{
		accessorKey: "location",
		header: "Location",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				{formatLocation(row.original.location)}
			</div>
		),
	},
	{
		accessorKey: "employmentType",
		header: "Type",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				{formatEmploymentType(row.original.employmentType)}
			</div>
		),
	},
	{
		accessorKey: "salary",
		header: "Salary",
		cell: ({ row }) => {
			const salary = row.original.salary;
			if (!salary)
				return (
					<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
						-
					</div>
				);

			const displaySalary = salary.cap
				? `${formatAsCurrency(
						salary.starting,
						salary.currencyCode
				  )} - ${formatAsCurrency(salary.cap, salary.currencyCode)}`
				: formatAsCurrency(salary.starting, salary.currencyCode);

			return (
				<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
					{displaySalary}
				</div>
			);
		},
	},
	{
		accessorKey: "createdAt",
		header: "Date",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				{new Date(row.original.createdAt).toISOString().split("T")[0]}
			</div>
		),
	},
	{
		id: "actions",
		header: "Actions",
		cell: ({ row }) => {
			const hasApplied = row.original.hasApplied && !row.original.canApply;
			return (
				<div className="py-2.5">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<MoreVertical className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem asChild>
								<Link to={`/opportunities/communities/${row.original.id}`}>
									View Details
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem asChild disabled={hasApplied}>
								<Link
									to={`/opportunities/communities/${row.original.id}/apply`}
									className={hasApplied ? "opacity-50 cursor-not-allowed" : ""}
								>
									{hasApplied ? "Applied" : "Apply"}
								</Link>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			);
		},
	},
];

export const communityAnnouncementsColumns: ColumnDef<
	GetCommunityAnnouncementsForProQuery["getCommunityAnnouncementsForPro"][0]
>[] = [
	{
		accessorKey: "title",
		header: "Title",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[14px] lg:text-[16px] text-gray-500">
				<Link
					to={`/communities/announcements/${row.original.id}`}
					className="font-medium hover:underline"
				>
					{row.original.title}
				</Link>
			</div>
		),
	},
	{
		accessorKey: "type",
		header: "Type",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				{formatAnnouncementType(row.original.type)}
			</div>
		),
	},
	{
		accessorKey: "organization.name",
		header: "Community",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				{row.original.organization.name}
			</div>
		),
	},
	{
		accessorKey: "publishedAt",
		header: "Published",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				{row.original.publishedAt
					? new Date(row.original.publishedAt).toISOString().split("T")[0]
					: "Draft"}
			</div>
		),
	},
	{
		id: "actions",
		header: "Actions",
		cell: ({ row }) => {
			return (
				<div className="py-2.5">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<MoreVertical className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem asChild>
								<Link to={`/communities/announcements/${row.original.id}`}>
									View Details
								</Link>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			);
		},
	},
];
